// ** Third Party Imports
import { useLocation, useNavigate } from "react-router-dom"

// ** UI Components
import { Card, CardContent } from "@components/common/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"

// ** Types
import { DictType, SubTab } from "@pma/types"

// ** Utils
import { cn } from "@pma/utils/helpers"
import { financeTabOption, financeTabs } from "@pma/utils/constants"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useTabsStore } from "@pma/stores/tabs-store"

// ** Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import TabViewSettings from "./tab-view-settings"
import ProjectFinancialRoles from "./project-financial-roles"
import ProjectQuoteManagerTab from "./project-quotes-manager-tab"
import ProjectSignatureSettings from "./project-signature-settings"
import { MainBoqsTable } from "@components/financials/main-boqs-table"

interface Props {
  project: DictType
  revalidate: CallableFunction
}

const subTabComponents: Record<string, (projectId: string, project: DictType) => JSX.Element> = {
  boq: () => <MainBoqsTable />,
  quotes: (projectId) => <ProjectQuoteManagerTab projectId={projectId} />,
  roles: (projectId) => <ProjectFinancialRoles projectId={projectId} />,
  signatures: (projectId, project) => (
    <ProjectSignatureSettings
      projectId={projectId}
      signaturesRequirement={project?.signatures_requirement}
    />
  ),
}

export const ProjectFinancialTabs = ({ project, revalidate }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { tabs: stateTabs } = useTabsStore()
  const { translate, locale } = useTranslator()
  const { authConfig, setTabManager } = useAuth()

  const projectId = project?.id
  const isHebrew = locale === "he"
  const allStateTabs = authConfig?.tabs ?? {}
  const financeStateTabs = stateTabs["finance"]
  const hasStateTabs = financeStateTabs?.length > 0
  const projectFinancialActiveTab = allStateTabs ? allStateTabs["project-detail"] : undefined
  const activeTabs: DictType = projectFinancialActiveTab || { main: "finance", sub: "boq" }
  const activeSub = activeTabs?.sub

  return (
    <Card className='overflow-x-hidden'>
      <CardContent className='p-2'>
        <Tabs
          defaultValue={activeSub}
          className={cn("relative w-full max-w-[90vw] overflow-x-auto", isHebrew ? "rtl" : "ltr")}
        >
          <TabsList className='flex w-full items-center justify-center rounded-full bg-transparent'>
            {financeTabs.map(({ label, value, disabled }) => (
              <TabsTrigger
                key={value}
                value={value}
                disabled={disabled}
                className={cn(
                  "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                  financeStateTabs?.includes(label) ? "flex" : !hasStateTabs ? "flex" : "hidden",
                  activeSub === value && "border-b-2",
                )}
                onClick={() => {
                  setTabManager({ group: "project-detail", main: "finance", sub: value, ...(value === "boq" && { subChild: "" }) })
                  navigate(location.pathname)
                }}
              >
                {translate(label)}
              </TabsTrigger>
            ))}
            <TabViewSettings options={financeTabOption} context='finance' />
          </TabsList>

          <TabsContent forceMount value='boq' className='p-0'>
            {subTabComponents[activeSub]?.(projectId, project)}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
